

import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { useSnackbar } from "notistack";
import {
  findCustomModule,
  updateCustomModuleInPrefs,
} from "sidenavCatalog";

function CustomModulePage() {
  const { moduleId } = useParams();
  const { axiosInstance } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [module, setModule] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [draftBody, setDraftBody] = useState("");
  const [draftName, setDraftName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/users/me/preferences");
      const sidenavPrefs = res.data?.sidenav_preferences || null;
      setPrefs(sidenavPrefs);
      const found = findCustomModule(sidenavPrefs, moduleId);
      setModule(found);
      setDraftBody(found?.body || "");
      setDraftName(found?.name || "");
    } catch (err) {
      console.error("Failed to load custom module", err);
      setModule(null);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, moduleId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!prefs || !module) return;
    setSaving(true);
    try {
      const nextPrefs = updateCustomModuleInPrefs(prefs, moduleId, {
        name: draftName.trim() || module.name,
        body: draftBody,
      });
      const res = await axiosInstance.patch("/users/me/preferences", {
        sidenav_preferences: nextPrefs,
      });
      const saved = res.data?.sidenav_preferences || nextPrefs;
      setPrefs(saved);
      const found = findCustomModule(saved, moduleId);
      setModule(found);
      setEditing(false);
      window.dispatchEvent(
        new CustomEvent("sidenavPreferencesUpdated", { detail: saved })
      );
      enqueueSnackbar("Module saved", { variant: "success" });
    } catch (err) {
      console.error("Failed to save custom module", err);
      enqueueSnackbar("Failed to save module", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!module || module.hidden) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h5" gutterBottom>
            Module not found
          </MDTypography>
          <MDTypography variant="body2" color="text">
            This custom module does not exist in your sidebar preferences, or it has been hidden.
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <Icon fontSize="large">{module.icon || "article"}</Icon>
            {editing ? (
              <TextField
                size="small"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            ) : (
              <MDTypography variant="h4">{module.name}</MDTypography>
            )}
          </MDBox>
          <MDBox display="flex" gap={1}>
            {editing ? (
              <>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setDraftBody(module.body || "");
                    setDraftName(module.name || "");
                    setEditing(false);
                  }}
                  disabled={saving}
                >
                  Cancel
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </MDButton>
              </>
            ) : (
              <MDButton variant="gradient" color="info" onClick={() => setEditing(true)}>
                Edit
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        {module.sectionTitle && (
          <MDTypography variant="caption" color="text" mb={2} display="block">
            Section: {module.sectionTitle}
          </MDTypography>
        )}

        {editing ? (
          <TextField
            fullWidth
            multiline
            minRows={12}
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="Write notes or content for this module…"
          />
        ) : (
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              typography: "body1",
              color: "text.primary",
              minHeight: 120,
              p: 2,
              borderRadius: 1,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {module.body?.trim()
              ? module.body
              : "No content yet. Click Edit to add notes for this module."}
          </Box>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default CustomModulePage;
